import { DateOnly } from "@hantera/design-system";
import {BackendFetch, GraphQueryNavigation, JsonTableResponse} from "@hantera/portal-app";
import Decimal from "decimal.js";

export interface BackendGraph {
  queryTable(query: GraphQueryNavigation, abortSignal?: AbortSignal, validation?: GraphQueryValidation): Promise<JsonTableResponse>
  /**
   * Queries the graph and flattens navigated nodes
   */
  queryGraph<T>(query: GraphQueryNavigation, abortSignal?: AbortSignal): Promise<Array<T>>
  /**
   * Queries the graph and returns an unprocessed response
   */
  queryRaw(query: GraphQueryNavigation, format?: string, abortSignal?: AbortSignal, validation?: GraphQueryValidation): Promise<Response>
}

export type GraphQueryValidation = 'strict' | 'warn'

export default function (portalFetch: BackendFetch): BackendGraph {

  async function queryTable(query: GraphQueryNavigation, abortSignal?: AbortSignal, validation?: GraphQueryValidation) {
    const res = await queryRaw(query, 'jsontable', abortSignal, validation);

    const data = await res.json() as Record<string, JsonTableResponse>;

    return data[query.edge];
  }
  async function queryGraph<T>(query: GraphQueryNavigation, abortSignal?: AbortSignal) {
    const response = await queryRaw(query, undefined, abortSignal);

    const result = parseGraphJson<T>(await response.text());
    return result[query.edge] as Array<T>;
  }

  async function  queryRaw(query: GraphQueryNavigation, format?: string, abortSignal?: AbortSignal, validation?: GraphQueryValidation) {
    const params = new URLSearchParams();
    if (format) {
      params.set('format', format);
    }
    if (validation) {
      params.set('validation', validation);
    }
    const queryString = params.size > 0 ? '?' + params.toString() : '';

    const response = await portalFetch('/core/resources/graph' + queryString, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify([query]),
      signal: abortSignal
    });

    if (!response.ok) {
      throw await response.errors()
    }

    return response;
  }

  return {
    queryTable,
    queryGraph,
    queryRaw,
  };

}

interface QueryNodeBody {
  fields?: string[]
  navigate?: {
    edge: string
    node: QueryNodeBody
  }[]
}

/**
 * Merges an append-only extension into a base graph query node. Fields are
 * unioned (deduplicated) and navigations are merged recursively by edge. The
 * base node is never mutated and nothing is ever removed from it.
 */
export function mergeQueryNode<T extends QueryNodeBody>(base: T, extension: QueryNodeBody): T {
  const result: QueryNodeBody = { ...base };

  if (extension.fields?.length) {
    const fields = [...(base.fields ?? [])];
    for (const field of extension.fields) {
      if (!fields.includes(field)) {
        fields.push(field);
      }
    }
    result.fields = fields;
  }

  if (extension.navigate?.length) {
    const navigate = (base.navigate ?? []).map(n => ({ ...n }));
    for (const extNav of extension.navigate) {
      const existing = navigate.find(n => n.edge === extNav.edge);
      if (existing) {
        existing.node = mergeQueryNode(existing.node, extNav.node);
      } else {
        navigate.push({
          edge: extNav.edge,
          node: mergeQueryNode({}, extNav.node)
        });
      }
    }
    result.navigate = navigate;
  }

  return result as T;
}

const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
const isoInstantRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|\+00:00)$/;


export function parseGraphJson<T>(jsonString: string) {
  return JSON.parse(jsonString,
    (key, value) => {

      if (typeof value === 'number')
      {
        return new Decimal(value);
      }
    
      
      if (typeof value === 'string') {
        if (isoInstantRegex.test(value)) {
          return new Date(value);
        }
        if (isoDateRegex.test(value)) {
          return DateOnly.parse(value);
        }
      }

      if (typeof value === 'object' && value !== null && Array.isArray(value.nodes)) {
        return value.nodes;
      }
      
      return value;
    }
  );
}